import React, { useState, useContext, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import { 
  FaVideo, FaLink, FaSignOutAlt, FaUser, 
  FaPlay, FaPlus, FaTimes, FaQuestionCircle,
  FaInfoCircle, FaHistory, FaCalendarAlt
} from 'react-icons/fa';
import { SERVER_URL } from '../config';

const Home = () => {
  const { user, isAuthenticated, logout, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [streamTitle, setStreamTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [joinStreamId, setJoinStreamId] = useState('');
  const [showStartModal, setShowStartModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);
  
  const startModalRef = useRef(null);
  const joinModalRef = useRef(null);
  
  // Handle click outside to close modals
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (startModalRef.current && !startModalRef.current.contains(event.target)) {
        setShowStartModal(false);
      }
      if (joinModalRef.current && !joinModalRef.current.contains(event.target)) {
        setShowJoinModal(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const startStream = async () => {
    if (!isAuthenticated || !user) {
      alert('Please log in to start a stream');
      navigate('/login');
      return;
    }
    
    if (!streamTitle) {
      alert('Please enter a stream title');
      return;
    }
    
    try {
      // Add logging to debug the request
      console.log('Starting stream with:', {
        title: streamTitle,
        hostUsername: user.username
      });
      
      // Use axios instead of fetch to handle SSL certificate validation issues
      const response = await axios.post(`${SERVER_URL}/streams`, {
        title: streamTitle,
        hostUsername: user.username
      }, {
        // Disable SSL certificate validation for development
        httpsAgent: new (require('https').Agent)({ rejectUnauthorized: false }),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = response.data;
      console.log('Stream created successfully:', data);
      
      // Use streamId instead of _id for navigation
      navigate(`/stream/${data.streamId}`, {
        state: {
          streamId: data.streamId,
          title: data.title
        }
      });
    } catch (error) {
      console.error('Error starting stream:', error);
      alert('Failed to start stream. Please try again: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <PageContainer>
      {isAuthenticated ? (
        <>
          <Header>
            <LogoContainer>
              <FaVideo />
              <AppName>Video Conferencing App</AppName>
            </LogoContainer>
            
            <HeaderControls>
              <RecordingsLink to="/recordings">
                <FaPlay />
                <span>My Recordings</span>
              </RecordingsLink>
              
              <NavButton onClick={() => navigate('/meeting-history')}>
                <FaHistory />
                <span>Meeting History</span>
              </NavButton>
              
              <NavButton onClick={() => navigate('/calendar')}>
                <FaCalendarAlt />
                <span>Calendar</span>
              </NavButton>
              
              <UserInfo>
                <UserAvatar>
                  {user.username.substring(0, 2).toLowerCase()}
                </UserAvatar>
                <span>{user.username}</span>
              </UserInfo>
              
              <LogoutButton onClick={handleLogout}>
                <FaSignOutAlt />
                <span>Logout</span>
              </LogoutButton>
            </HeaderControls>
          </Header>

          <MainContent>
            <WelcomeSection>
              <WelcomeTitle>Welcome back, <span>{user.username}</span>!</WelcomeTitle>
              <WelcomeSubtitle>Conference, Collaborate, and Connect</WelcomeSubtitle>
              <WelcomeDescription>
                This platform lets you host and join live video meetings in real time. 
                Get started with a single click.
              </WelcomeDescription>
            </WelcomeSection>
            
            <ActionContainer>
              <ActionCard>
                <ActionIcon color="var(--primary)">
                  <FaPlus onClick={() => setShowStartModal(true)}/>
                </ActionIcon>
                <ActionLabel>Start a New Meeting</ActionLabel>
                <ActionDescription>
                  Create your own video meeting and invite others to join.
                </ActionDescription>
                <ActionButton 
                  color="var(--gradient-primary)" 
                  onClick={() => setShowStartModal(true)}
                >
                  Start Meeting
                </ActionButton>
              </ActionCard>
              
              <ActionCard>
                <ActionIcon color="var(--accent)">
                  <FaLink onClick={() => setShowJoinModal(true)}/>
                </ActionIcon>
                <ActionLabel>Join an Existing Meeting</ActionLabel>
                <ActionDescription>
                  Enter a meeting ID to join an ongoing video conference.
                </ActionDescription>
                <ActionButton 
                  color="var(--gradient-accent)" 
                  onClick={() => setShowJoinModal(true)}
                >
                  Join Meeting
                </ActionButton>
              </ActionCard>
            </ActionContainer>
            
            <HelpButton 
              onClick={() => setShowHelpTooltip(!showHelpTooltip)}
              aria-label="Help"
            >
              <FaInfoCircle />
              {showHelpTooltip && (
                <HelpTooltip>
                  <p>Start a new meeting or join an existing one using the meeting ID.</p>
                  <p>Your recordings can be accessed from the "My Recordings" link.</p>
                </HelpTooltip>
              )}
            </HelpButton>
          </MainContent>
          
          {/* Start Meeting Modal */}
          {showStartModal && (
            <ModalOverlay>
              <Modal ref={startModalRef} className="glass">
                <ModalHeader>
                  <h3>Start a New Meeting</h3>
                  <CloseButton onClick={() => setShowStartModal(false)}>
                    <FaTimes />
                  </CloseButton>
                </ModalHeader>
                <ModalContent>
                  <InputContainer>
                    <InputIcon>
                      <FaVideo />
                    </InputIcon>
                    <StyledInput
                      type="text"
                      value={streamTitle}
                      onChange={(e) => setStreamTitle(e.target.value)}
                      placeholder="Enter Meeting Title"
                    />
                  </InputContainer>
                  <PrimaryButton onClick={startStream} disabled={isCreating}>
                    {isCreating ? 'Starting...' : 'Start Now'}
                  </PrimaryButton>
                </ModalContent>
              </Modal>
            </ModalOverlay>
          )}
          
          {/* Join Meeting Modal */}
          {showJoinModal && (
            <ModalOverlay>
              <Modal ref={joinModalRef} className="glass">
                <ModalHeader>
                  <h3>Join an Existing Meeting</h3>
                  <CloseButton onClick={() => setShowJoinModal(false)}>
                    <FaTimes />
                  </CloseButton>
                </ModalHeader>
                <ModalContent>
                  <InputContainer>
                    <InputIcon>
                      <FaLink />
                    </InputIcon>
                    <StyledInput
                      type="text"
                      value={joinStreamId}
                      onChange={(e) => setJoinStreamId(e.target.value)}
                      placeholder="Enter Meeting ID"
                    />
                  </InputContainer>
                  <PrimaryButton 
                    onClick={() => navigate(`/view/${joinStreamId}`, { 
                      state: { username: user.username } 
                    })}
                  >
                    Join Now
                  </PrimaryButton>
                </ModalContent>
              </Modal>
            </ModalOverlay>
          )}
        </>
      ) : (
        <LoginPrompt className="glass">
          <p>Please log in to start or join a meeting</p>
          <PrimaryButton onClick={() => navigate('/login')}>Go to Login</PrimaryButton>
        </LoginPrompt>
      )}
    </PageContainer>
  );
};

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--gradient-bg);
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-sm);
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    gap: 1rem;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 1.2rem;
  color: var(--primary);
  animation: ${fadeIn} 0.5s ease;
  text-shadow: 0 0 10px rgba(15, 98, 254, 0.3);
  
  svg {
    font-size: 1.5rem;
    filter: drop-shadow(0 0 5px rgba(15, 98, 254, 0.3));
  }
`;

const AppName = styled.span`
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
  
  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const RecordingsLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  transition: var(--transition-normal);
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }
  
  svg {
    color: var(--success);
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    
    span {
      display: none;
    }
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  
  @media (max-width: 480px) {
    span {
      display: none;
    }
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--primary-light);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: lowercase;
  font-weight: 500;
  transition: var(--transition-normal);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: var(--shadow-md);
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--gradient-primary);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition-normal);
  box-shadow: var(--shadow-sm);
  
  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    
    span {
      display: none;
    }
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const WelcomeSection = styled.div`
  text-align: center;
  max-width: 800px;
  margin-bottom: 3rem;
  animation: ${slideUp} 0.5s ease;
`;

const WelcomeTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
  
  span {
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const WelcomeSubtitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const WelcomeDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-tertiary);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ActionContainer = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const ActionCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  transition: var(--transition-normal);
  width: 280px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-lg);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 400px;
    padding: 1.5rem;
  }
`;

const ActionIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${props => props.color || 'var(--primary)'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: var(--shadow-md);
  transition: var(--transition-normal);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: var(--shadow-lg);
  }
  
  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
  }
`;

const ActionLabel = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
  text-align: center;
`;

const ActionDescription = styled.p`
  font-size: 0.95rem;
  color: var(--text-tertiary);
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const ActionButton = styled.button`
  background: ${props => props.color || 'var(--primary)'};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition-normal);
  box-shadow: var(--shadow-sm);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const HelpButton = styled.button`
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.7);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  cursor: pointer;
  transition: var(--transition-normal);
  box-shadow: var(--shadow-sm);
  z-index: 100;
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
    box-shadow: var(--shadow-md);
    transform: scale(1.1);
  }
`;

const HelpTooltip = styled.div`
  position: absolute;
  bottom: 50px;
  right: 0;
  width: 250px;
  background: white;
  padding: 1rem;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  font-size: 0.875rem;
  color: var(--text-secondary);
  z-index: 10;
  animation: ${fadeIn} 0.3s ease;
  
  p {
    margin-bottom: 0.5rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;
  
  @media (max-width: 768px) {
    align-items: flex-end;
  }
`;

const Modal = styled.div`
  width: 100%;
  max-width: 450px;
  border-radius: var(--radius-lg);
  overflow: hidden;
  animation: ${scaleIn} 0.3s ease;
  
  @media (max-width: 768px) {
    max-width: 100%;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    animation: ${slideUp} 0.3s ease;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  
  h3 {
    margin: 0;
    color: var(--text-primary);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.25rem;
  color: var(--text-tertiary);
  cursor: pointer;
  transition: var(--transition-fast);
  
  &:hover {
    color: var(--error);
  }
`;

const ModalContent = styled.div`
  padding: 1.5rem;
`;

const InputContainer = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-tertiary);
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--neutral-300);
  border-radius: var(--radius-md);
  font-size: 1rem;
  transition: var(--transition-fast);
  
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(15, 98, 254, 0.2);
  }
  
  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const PrimaryButton = styled.button`
  width: 100%;
  background: var(--gradient-primary);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition-normal);
  box-shadow: var(--shadow-sm);
  
  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoginPrompt = styled.div`
  margin: auto;
  padding: 2rem;
  border-radius: var(--radius-lg);
  text-align: center;
  max-width: 400px;
  animation: ${scaleIn} 0.3s ease;
  
  p {
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
    color: var(--text-secondary);
  }
`;
const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  transition: var(--transition-normal);
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }
  
  svg {
    color: var(--success);
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    
    span {
      display: none;
    }
  }
`;

export default Home;
